import { useRecycleBinStore } from "../store/recycleBinStore.ts";
import { userTimezoneHeaders } from "./userTimezoneHeader";

type RecycleBinTableEntry = { total?: number; items?: unknown[] };

/**
 * Updates Zustand recycle-bin counts from GET /recycle-bin `tables` (same logic as RecycleBinTab).
 */
export function applyRecycleBinTableTotalsToStore(
  tables: Record<string, RecycleBinTableEntry>,
): void {
  const faceEntry = tables.face_enrollment;
  const faceCount =
    typeof faceEntry?.total === "number"
      ? faceEntry.total
      : Array.isArray(faceEntry?.items)
        ? faceEntry.items.length
        : 0;
  const totals: Record<string, number> = {};
  for (const [key, data] of Object.entries(tables)) {
    if (data && typeof data === "object") {
      totals[key] =
        typeof data.total === "number"
          ? data.total
          : Array.isArray(data.items)
            ? data.items.length
            : 0;
    } else {
      totals[key] = 0;
    }
  }
  useRecycleBinStore.getState().setFacesRecycleBinCount(faceCount);
  useRecycleBinStore.getState().setRecycleBinTableTotals(totals);
}

/**
 * GET /recycle-bin and push totals into the store (Recycle Bin tab badge).
 * Safe to call after soft-delete from any screen; failures are ignored.
 */
export async function fetchAndApplyRecycleBinTotals(): Promise<void> {
  const base = (process.env.REACT_APP_API_URL_FOR_AI_DETECTION || "").replace(
    /\/$/,
    "",
  );
  if (!base) return;
  try {
    const res = await fetch(`${base}/recycle-bin`, {
      method: "GET",
      headers: { Accept: "application/json", ...userTimezoneHeaders() },
    });
    const text = await res.text();
    const trimmed = text.trim();
    if (!res.ok || !trimmed || trimmed.startsWith("<")) return;
    const body = JSON.parse(trimmed) as {
      data?: { tables?: Record<string, RecycleBinTableEntry> };
      tables?: Record<string, RecycleBinTableEntry>;
    };
    const tables =
      body?.data && typeof body.data.tables === "object"
        ? (body.data.tables ?? {})
        : typeof body?.tables === "object"
          ? (body.tables ?? {})
          : {};
    applyRecycleBinTableTotalsToStore(tables);
  } catch {
    /* keep previous totals */
  }
}
